// Add a worker, as an address.
//
// This was a ResponsiveDialog capped at `sm:max-w-2xl`, which on mobile put the
// SSH step — a textarea holding a multi-line private key — inside a scrolling
// box inside a drawer, with the footer competing for the same space. It was also
// unaddressable: a refresh or a stray Escape lost everything typed.
//
// As a page it gets the wizard shell the setup flow uses, `?target=remote` for a
// deep link straight to the SSH path, and a guard so a half-pasted key survives
// a misclick in the sidebar.

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@veil/ui/components/alert-dialog";
import { Button } from "@veil/ui/components/button";
import ServerCog from "lucide-react/dist/esm/icons/server-cog";
import { useState } from "react";
import { useLocation } from "wouter";
import type { WizardStep } from "@/components/wizard/wizard-shell";
import { WizardShell } from "@/components/wizard/wizard-shell";
import { useSystemVersion } from "@/lib/system-version";
import { useLeaveGuard } from "@/lib/use-leave-guard";
import { DEPLOY_TARGETS, type DeployTarget } from "./deploy/schema";
import { useAddWorkerWizard } from "./deploy/use-add-worker-wizard";

/** `?target=remote` preselects the SSH path; anything else is ignored. */
function targetFromSearch(): DeployTarget | undefined {
	const raw = new URLSearchParams(window.location.search).get("target");
	return DEPLOY_TARGETS.find((t) => t === raw);
}

export function AddWorkerPage() {
	const [, navigate] = useLocation();
	// Read once: this seeds a form default, and re-reading it as the operator
	// changes their mind would fight them for the radio group.
	const [initialTarget] = useState(targetFromSearch);
	const version = useSystemVersion().data?.version;

	const {
		wizard,
		meta,
		stepNode,
		submit,
		submitLabel,
		pending,
		dirty,
		summary,
	} = useAddWorkerWizard({
		onStarted: (operationId) => handOff(`/operations/${operationId}`),
		onRegistered: (hostname) => handOff(`/workers/${hostname}`),
		initialTarget,
	});

	const guard = useLeaveGuard(dirty);

	// Declared, not assigned, so the two callbacks above can reach it: they only
	// ever run after this component body has finished.
	function handOff(href: string) {
		// The worker has been handed to the server. The form is still dirty and
		// there is nothing left to lose by leaving.
		guard.release();
		navigate(href);
	}

	const steps: WizardStep[] = wizard.steps.map((s) => ({
		id: s.id,
		label: s.label,
	}));

	return (
		<form onSubmit={submit}>
			<WizardShell
				variant="page"
				steps={steps}
				currentIndex={wizard.currentIndex}
				onStepSelect={(index) => {
					const step = wizard.steps[index];
					if (step) wizard.goTo(step.id);
				}}
				railTitle="Add a worker"
				railIcon={ServerCog}
				title={meta.title}
				description={meta.blurb}
				why={meta.why}
				summary={summary}
				version={version}
				stepKey={wizard.current.id}
				footer={
					<div className="flex items-center justify-between gap-2">
						<Button
							type="button"
							variant="ghost"
							onClick={() =>
								wizard.isFirst
									? guard.request("/workers", navigate)
									: wizard.back()
							}
							disabled={pending}
						>
							{wizard.isFirst ? "Cancel" : "Back"}
						</Button>
						{wizard.isLast ? (
							<Button type="submit" disabled={pending}>
								{submitLabel}
							</Button>
						) : (
							<Button
								type="button"
								onClick={() => {
									void wizard.next();
								}}
							>
								Continue
							</Button>
						)}
					</div>
				}
			>
				{stepNode}
			</WizardShell>

			<AlertDialog
				open={guard.pendingHref !== null}
				onOpenChange={(open) => {
					if (!open) guard.cancel();
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Leave without adding the worker?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Nothing has been created yet, and what you have filled in — the
							name, and any SSH key you pasted — is not saved anywhere.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep editing</AlertDialogCancel>
						<AlertDialogAction onClick={() => guard.confirm(navigate)}>
							Discard and leave
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</form>
	);
}
